<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { useDatePicker } from '@/shared/lib/use/useDatePicker';
  import { useDateValidation } from '@/shared/lib';
  import DatePickerHeader from '@/shared/ui/datepicker/DatePickerHeader.vue';
  import Weekdays from '@/shared/ui/datepicker/Weekdays.vue';
  import DateGrid from '@/shared/ui/datepicker/DateGrid.vue';
  import DatePickerButtons from '@/shared/ui/datepicker/DatePickerButtons.vue';

  const props = withDefaults(
    defineProps<{
      selectedDate: Date | null;
      isOpen: boolean;
      disablePastDates?: boolean;
      disableFutureDates?: boolean;
      defaultToToday?: boolean;
      minDate?: Date;
    }>(),
    {
      defaultToToday: true,
    }
  );

  const emit = defineEmits(['update:selectedDate', 'update:isOpen', 'apply', 'cancel']);

  const isValidDate = (value: unknown): value is Date => {
    return value instanceof Date && !Number.isNaN(value.getTime());
  };

  const getSafeDate = (value: Date | null | undefined): Date | null => {
    return isValidDate(value) ? value : null;
  };

  const tempSelectedDate = ref<Date | null>(getSafeDate(props.selectedDate));
  const { validateDateNotPast, validateDateNotFuture } = useDateValidation();

  watch(
    () => props.selectedDate,
    (newDate) => {
      tempSelectedDate.value = getSafeDate(newDate);
    },
    { immediate: true }
  );

  watch(
    () => props.isOpen,
    (isOpen) => {
      const safeSelectedDate = getSafeDate(props.selectedDate);

      if (isOpen && safeSelectedDate === null && props.defaultToToday) {
        tempSelectedDate.value = props.minDate ?? new Date();
      } else if (isOpen) {
        tempSelectedDate.value = safeSelectedDate;
      }
    },
    { immediate: true }
  );

  const { currentDate, currentMonthYear, calendarDays, goToNextMonth, goToPrevMonth } = useDatePicker(
    getSafeDate(props.selectedDate) ?? new Date()
  );

  watch(
    () => props.selectedDate,
    (newDate) => {
      const safeSelectedDate = getSafeDate(newDate);
      if (safeSelectedDate) {
        currentDate.value = new Date(safeSelectedDate);
      }
    },
    { immediate: true }
  );

  const isBeforeMinDate = (date: Date): boolean => {
    if (!props.minDate) return false;
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const min = new Date(props.minDate);
    min.setHours(0, 0, 0, 0);
    return checkDate < min;
  };

  const selectDate = (date: Date) => {
    if (props.disablePastDates && !validateDateNotPast(date)) return;
    if (props.disableFutureDates && !validateDateNotFuture(date)) return;
    if (isBeforeMinDate(date)) return;
    tempSelectedDate.value = date;
  };

  const isSelected = (date: Date) => {
    if (!tempSelectedDate.value || !(tempSelectedDate.value instanceof Date)) return false;

    return (
      date.getDate() === tempSelectedDate.value.getDate() &&
      date.getMonth() === tempSelectedDate.value.getMonth() &&
      date.getFullYear() === tempSelectedDate.value.getFullYear()
    );
  };

  const isDateDisabled = (date: Date) => {
    if (props.disablePastDates && !validateDateNotPast(date)) return true;
    if (props.disableFutureDates && !validateDateNotFuture(date)) return true;
    if (isBeforeMinDate(date)) return true;
    return false;
  };

  const applyDate = () => {
    emit('update:selectedDate', tempSelectedDate.value);
    emit('apply', tempSelectedDate.value);
    emit('update:isOpen', false);
  };

  const cancelSelection = () => {
    tempSelectedDate.value = getSafeDate(props.selectedDate);
    emit('cancel');
    emit('update:isOpen', false);
  };

  defineExpose({ tempSelectedDate });
</script>

<template>
  <VueFinalModal
    :model-value="isOpen"
    class="flex justify-center items-center"
    content-class="bg-white-01 rounded-lg shadow-lg w-[360px] overflow-hidden"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @update:model-value="$emit('update:isOpen', $event)"
  >
    <div class="datepicker-container">
      <DatePickerHeader
        :current-month-year="currentMonthYear()"
        @prev-month="goToPrevMonth"
        @next-month="goToNextMonth"
      />
      <div class="p-4">
        <Weekdays />
        <DateGrid
          :calendar-days="calendarDays"
          :select-date="selectDate"
          :is-selected="isSelected"
          :is-date-disabled="isDateDisabled"
        />
      </div>

      <DatePickerButtons
        @apply="applyDate"
        @cancel="cancelSelection"
      />
    </div>
  </VueFinalModal>
</template>
